<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useMallCategoryList } from '@/api/queries'
import type { MallCategoryListRow } from '@/types/api'

const props = defineProps<{
  /** 対象モール表示名 */
  mallName: string
  /** 対象モールのDB名 */
  dbName: string
  /** 編集単位: browsenode(CM2) か asin(CM3) か */
  scope: 'browsenode' | 'asin'
  /** 見出しに出す対象(ブラウズノードID or ASIN) */
  targetLabel: string
  /** 現在割当済みのカテゴリID(初期選択に使う) */
  currentCategoryId?: string
  /** 現在の有効フラグ */
  currentEnableFlg?: string
  /** 削除可能か(scope=asin のときのみ) */
  deletable?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'save', payload: { categoryId: string; categoryName: string; enableFlg: '0' | '1' }): void
  (e: 'delete'): void
}>()

const keywordInput = ref('')
const keyword = ref('')
let debounceTimer: ReturnType<typeof setTimeout> | undefined
watch(keywordInput, (v) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => (keyword.value = v.trim()), 350)
})

const dbNameRef = computed(() => props.dbName)
const enabled = ref(true)
const { data, isFetching } = useMallCategoryList(dbNameRef, keyword, enabled)

const items = computed(() => data.value?.items ?? [])
const truncated = computed(() => data.value?.truncated ?? false)

const selected = ref<MallCategoryListRow | null>(null)
// 有効/無効ラジオ(初期値は現在値、なければ有効)
const enableFlg = ref<'0' | '1'>(props.currentEnableFlg === '0' ? '0' : '1')

function pick(row: MallCategoryListRow) {
  selected.value = row
}

function confirm() {
  if (!selected.value) return
  emit('save', {
    categoryId: selected.value.CategoryId,
    categoryName: selected.value.CategoryName ?? '',
    enableFlg: enableFlg.value,
  })
}
</script>

<template>
  <div class="modal-overlay" @click.self="emit('close')">
    <div class="modal bn-modal">
      <div class="modal-head">
        <div class="modal-title">
          カテゴリを{{ scope === 'asin' ? '割当（ASIN単位）' : '割当（ブラウズノード単位）' }}
        </div>
        <button class="modal-close" @click="emit('close')">✕</button>
      </div>

      <div class="bn-target">
        <span class="cat-mall-icon" style="width:18px;height:18px;">{{ mallName.slice(0, 1) }}</span>
        <span>{{ mallName }}</span>
        <span class="chip-id">{{ targetLabel }}</span>
      </div>

      <div class="bn-filter">
        <svg class="icon icon-sm" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input
          v-model="keywordInput"
          class="bn-filter-input"
          type="text"
          placeholder="カテゴリID・カテゴリ名で絞り込み（正規表現可）"
          autofocus
        />
        <span v-if="isFetching" class="bn-loading"><span class="spinner spinner-sm"></span> 検索中…</span>
      </div>

      <div v-if="truncated" class="bn-truncated">
        件数が多いため一部のみ表示しています。フィルタで絞り込んでください。
      </div>

      <div class="bn-list">
        <div v-if="items.length === 0 && !isFetching" class="empty-state" style="padding:20px 0;">
          該当するカテゴリがありません
        </div>
        <div
          v-for="row in items"
          :key="row.CategoryId"
          class="bn-item"
          :class="{ selected: selected?.CategoryId === row.CategoryId, assigned: currentCategoryId === row.CategoryId }"
          @click="pick(row)"
          @dblclick="pick(row); confirm()"
        >
          <span class="bn-item-id chip-id">{{ row.CategoryId }}</span>
          <span class="bn-item-route">{{ row.CategoryName || '（名称なし）' }}</span>
          <span v-if="currentCategoryId === row.CategoryId" class="bn-item-assigned">現在</span>
          <span v-if="row.EnableFlg === '0'" class="cat-flag-ng" title="無効カテゴリ">⚠</span>
        </div>
      </div>

      <div class="cat-enable-row">
        <span class="cat-enable-label">このカテゴリ割当の状態:</span>
        <label class="radio"><input type="radio" value="1" v-model="enableFlg"> 有効</label>
        <label class="radio"><input type="radio" value="0" v-model="enableFlg"> 無効</label>
      </div>

      <div class="modal-foot">
        <button
          v-if="deletable && scope === 'asin'"
          class="btn btn-danger"
          style="margin-right:auto;"
          @click="emit('delete')"
        >
          割当を削除
        </button>
        <button class="btn btn-ghost" @click="emit('close')">キャンセル</button>
        <button class="btn btn-primary" :disabled="!selected" @click="confirm">保存する</button>
      </div>
    </div>
  </div>
</template>
